import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download } from "lucide-react";

const KEY = "hifireels.install.banner.dismissed.v1";

/**
 * InstallAppBanner — mobile-only dismissible bottom banner that surfaces the
 * native Chrome `beforeinstallprompt` on hifireels.com. One-tap install.
 *
 * Behaviour:
 * - Auto-hides once the PWA is installed (standalone display mode or `appinstalled`).
 * - If user Dismisses, we DO NOT persist forever — we re-show every 10 min for 5s
 *   until they install (per user request: "keep popping every 10 mins for atleast
 *   5 seconds so user can know he has to install hifireels for full screen").
 * - Only fires on browsers that expose `beforeinstallprompt` (Chrome/Edge/Brave/Samsung
 *   Internet on Android; desktop Chrome/Edge). Firefox & Safari don't support it —
 *   that's a browser platform limitation no dev can override.
 */
export default function InstallAppBanner() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip if already installed (running as standalone/fullscreen) or on desktop
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.matchMedia("(display-mode: fullscreen)").matches) return;
    if (window.matchMedia("(min-width: 768px)").matches) return;

    let periodicId = null;
    const openFor5s = () => {
      setOpen(true);
      setTimeout(() => setOpen(false), 5000);
    };

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // First pop after 3s, then every 10 min for 5s
      setTimeout(openFor5s, 3000);
      periodicId = setInterval(openFor5s, 10 * 60 * 1000);
    };
    const onInstalled = () => {
      setOpen(false);
      if (periodicId) clearInterval(periodicId);
      localStorage.setItem(KEY, "1");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (periodicId) clearInterval(periodicId);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") localStorage.setItem(KEY, "1");
    } catch { /* ignore */ }
    setOpen(false); setDeferred(null);
  };
  const dismiss = () => { setOpen(false); /* re-shows in 10 min automatically */ };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="install-app-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed bottom-24 left-3 right-3 z-[75] md:hidden flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-[#FF2A5F]/95 to-[#FF7A00]/95 backdrop-blur border border-white/20 shadow-[0_10px_40px_rgba(255,42,95,0.35)]">
          <img src="/logo192.png" alt="Hifireels" className="w-11 h-11 rounded-xl object-cover flex-shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-outfit font-extrabold text-white leading-tight">Install Hifireels for true 100% fullscreen</div>
            <div className="text-[11px] text-white/85 leading-snug">One-tap install · Home-screen icon · Zero browser bars · Offline reels</div>
          </div>
          <button onClick={install} data-testid="install-app-cta"
            className="px-3 py-1.5 rounded-full bg-white text-[#FF2A5F] text-xs font-extrabold flex items-center gap-1 active:scale-95 flex-shrink-0">
            <Download className="w-3.5 h-3.5"/>Install
          </button>
          <button onClick={dismiss} data-testid="install-app-close"
            className="p-1 rounded-full bg-white/15 flex-shrink-0" aria-label="Dismiss">
            <X className="w-3.5 h-3.5 text-white"/>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
